// src/components/DateRangeSelector.jsx
import React, { useCallback, useEffect, useRef, useState } from 'react';

const DateRangeSelector = ({
  defaultRange = '30', // Default to 30 days
  onApplyFilters, // Callback function to apply the date filters
  includeAll = false,
  className = '',
  selectClassName = '',
  inputClassName = '',
}) => {
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '' });
  const [range, setRange] = useState(defaultRange);
  const onApplyFiltersRef = useRef(onApplyFilters);

  useEffect(() => {
    onApplyFiltersRef.current = onApplyFilters;
  }, [onApplyFilters]);

  const applyFilters = useCallback((dateFrom, dateTo) => {
    onApplyFiltersRef.current?.(dateFrom, dateTo);
  }, []);

  // Helper to set predefined ranges
  const setDateRange = useCallback((selectedRange) => {
    if (selectedRange === 'all') {
      setFilters({ dateFrom: '', dateTo: '' });
      applyFilters('', '');
      return;
    }

    const today = new Date();
    let startDate = new Date(today);

    if (selectedRange === '30') startDate.setDate(today.getDate() - 30);
    else if (selectedRange === '60') startDate.setDate(today.getDate() - 60);
    else if (selectedRange === '90') startDate.setDate(today.getDate() - 90);
    else if (selectedRange === '1y') startDate.setFullYear(today.getFullYear() - 1);

    const formattedStartDate = startDate.toISOString().split('T')[0];
    const formattedEndDate = today.toISOString().split('T')[0];

    setFilters({ dateFrom: formattedStartDate, dateTo: formattedEndDate });
    applyFilters(formattedStartDate, formattedEndDate);
  }, [applyFilters]);

  const handleRangeChange = (selectedRange) => {
    if (selectedRange === 'custom') {
      // Prefill custom with the implied prior range
      const today = new Date();
      let startDate = new Date(today);

      if (range === '30') startDate.setDate(today.getDate() - 30);
      else if (range === '60') startDate.setDate(today.getDate() - 60);
      else if (range === '90') startDate.setDate(today.getDate() - 90);
      else if (range === '1y') startDate.setFullYear(today.getFullYear() - 1);

      const formattedStartDate = startDate.toISOString().split('T')[0];
      const formattedEndDate = today.toISOString().split('T')[0];
      setFilters({ dateFrom: formattedStartDate, dateTo: formattedEndDate });
    } else {
      setDateRange(selectedRange);
    }
    setRange(selectedRange);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const next = { ...filters, [name]: value };
    setFilters(next);
    applyFilters(next.dateFrom, next.dateTo);
  };

  useEffect(() => {
    setRange(defaultRange);
    setDateRange(defaultRange);
  }, [defaultRange, setDateRange]);

  const showCustomRange = range === 'custom';

  return (
    <div className={`flex flex-wrap items-center gap-3 text-sm ${className}`}>
      <select
        className={`h-9 text-sm rounded-md border border-input bg-background text-foreground shadow-sm
                   focus:outline-none focus:ring-2 focus:ring-ring px-3 ${selectClassName}`}
        onChange={(e) => handleRangeChange(e.target.value)}
        value={range}
      >
        {includeAll && <option value="all">All History</option>}
        <option value="30">Last 30 Days</option>
        <option value="60">Last 60 Days</option>
        <option value="90">Last 90 Days</option>
        <option value="1y">Last Year</option>
        <option value="custom">Custom Range</option>
      </select>

      {showCustomRange && (
        <>
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">From</label>
            <input
              type="date"
              name="dateFrom"
              value={filters.dateFrom}
              onChange={handleFilterChange}
              className={`h-9 text-sm rounded-md border border-input bg-background text-foreground shadow-sm
                         focus:outline-none focus:ring-2 focus:ring-ring px-3 ${inputClassName}`}
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">To</label>
            <input
              type="date"
              name="dateTo"
              value={filters.dateTo}
              onChange={handleFilterChange}
              className={`h-9 text-sm rounded-md border border-input bg-background text-foreground shadow-sm
                         focus:outline-none focus:ring-2 focus:ring-ring px-3 ${inputClassName}`}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default DateRangeSelector;
